import { TimeHelper } from '../../../utils';
import { DaySummary, Service } from '../../../types';
import { ReportChartViewTypeEnum } from '../types';
import { getServicesKey } from './getServicesKey';

interface IBuildServiceStatsChartParams {
  data: Record<string, DaySummary>;
  dateRange: { from: number; to: number };
  firstService: Service;
  followupService: Service;
  timezone: string;
}

interface IGroupedCounts {
  groupIndexByKeyMap: Record<string, number>;
  labels: string[];
  firstServiceCounts: {
    total: number;
    grouped: number[];
  };
  followupServiceCounts: {
    total: number;
    grouped: number[];
  };
}

interface IGroupCounts {
  key: string;
  firstServiceCount: number;
  followupServiceCount: number;
}

function getWeekKey(dateUnix: number, timezone: string) {
  return String(TimeHelper.getStartOfWeek(dateUnix, timezone));
}

function getMonthKey(dateUnix: number, timezone: string) {
  return String(TimeHelper.getStartOfMonth(dateUnix, timezone));
}

function getWeekLabel(key: string, timezone: string) {
  return TimeHelper.toWeekStringFormat(Number(key), timezone);
}

function getMonthLabel(key: string, timezone: string) {
  return TimeHelper.toMonthStringFormat(Number(key), timezone);
}

function getGroupedCountsInitialValue(): IGroupedCounts {
  return {
    groupIndexByKeyMap: {},
    labels: [],
    firstServiceCounts: {
      total: 0,
      grouped: [],
    },
    followupServiceCounts: {
      total: 0,
      grouped: [],
    },
  };
}

function getDayCounts(
  dayStats: DaySummary | undefined,
  dateUnix: number,
  timezone: string,
  firstService: Service,
  followupService: Service,
  getKeyFn: (dateUnix: number, timezone: string) => string,
) {
  const key = getKeyFn(dateUnix, timezone);

  const result = {
    key,
    firstServiceCount: 0,
    followupServiceCount: 0,
  };

  if (!dayStats) return result;

  for (let serviceIndex = 0; serviceIndex < dayStats.services.length; serviceIndex += 1) {
    const serviceStats = dayStats.services[serviceIndex];
    const serviceStatsKey = getServicesKey(serviceStats.services);

    const total = serviceStats.appointments + serviceStats.slots + serviceStats.walkins;

    if (serviceStatsKey === firstService.id) {
      result.firstServiceCount += total;
    } else if (serviceStatsKey === followupService.id) {
      result.followupServiceCount += total;
    }
  }

  return result;
}

function addCountsToGroup(
  chartData: IGroupedCounts,
  dayCounts: IGroupCounts,
  timezone: string,
  getLabelFn: (key: string, timezone: string) => string,
): IGroupedCounts {
  const result: IGroupedCounts = { ...chartData };

  if (!result.groupIndexByKeyMap[dayCounts.key]) {
    result.groupIndexByKeyMap = { ...result.groupIndexByKeyMap, [dayCounts.key]: result.labels.length };
    result.labels = [...result.labels, getLabelFn(dayCounts.key, timezone)];
    result.firstServiceCounts.grouped = [...result.firstServiceCounts.grouped, 0];
    result.followupServiceCounts.grouped = [...result.followupServiceCounts.grouped, 0];
  }

  const groupIndex = result.groupIndexByKeyMap[dayCounts.key];

  result.firstServiceCounts.grouped = [...result.firstServiceCounts.grouped];
  result.firstServiceCounts.grouped[groupIndex] += dayCounts.firstServiceCount;
  result.firstServiceCounts.total += dayCounts.firstServiceCount;

  result.followupServiceCounts.grouped = [...result.followupServiceCounts.grouped];
  result.followupServiceCounts.grouped[groupIndex] += dayCounts.followupServiceCount;
  result.followupServiceCounts.total += dayCounts.followupServiceCount;

  return result;
}

function countsToChartData({ labels, firstServiceCounts, followupServiceCounts }: IGroupedCounts) {
  return {
    labels,
    datasets: [
      {
        label: `First opportunities scheduled or done (${firstServiceCounts.total})`,
        data: firstServiceCounts.grouped,
        backgroundColor: '#ff7971',
      },
      {
        label: `Follow-ups done (${followupServiceCounts.total})`,
        data: followupServiceCounts.grouped,
        backgroundColor: '#fcfda1',
      },
    ],
  };
}

export function buildServiceStatsChart({ data, dateRange, firstService, followupService, timezone }: IBuildServiceStatsChartParams) {
  let countsByWeek: IGroupedCounts = getGroupedCountsInitialValue();
  let countsByMonth: IGroupedCounts = getGroupedCountsInitialValue();

  const daysCount = TimeHelper.getDaysDiffUnix(dateRange.from, dateRange.to, timezone);

  for (let daysOffset = 0; daysOffset < daysCount; daysOffset += 1) {
    const dateUnix = TimeHelper.addDays(dateRange.from, daysOffset, timezone);
    const dateString = TimeHelper.unixToStandardFormat(dateUnix, timezone);
    const dayStats: DaySummary = data[dateString];

    const dayCountsByWeekKey = getDayCounts(dayStats, dateUnix, timezone, firstService, followupService, getWeekKey);
    countsByWeek = addCountsToGroup(countsByWeek, dayCountsByWeekKey, timezone, getWeekLabel);

    const dayCountsByMonthKey = getDayCounts(dayStats, dateUnix, timezone, firstService, followupService, getMonthKey);
    countsByMonth = addCountsToGroup(countsByMonth, dayCountsByMonthKey, timezone, getMonthLabel);
  }

  return {
    [ReportChartViewTypeEnum.WEEK]: countsToChartData(countsByWeek),
    [ReportChartViewTypeEnum.MONTH]: countsToChartData(countsByMonth),
  };
}
